import { useState } from 'react';
import {
  INCIDENT_SEVERITY_LABEL, INCIDENT_STATUS_LABEL, INCIDENT_STATUS_TONE,
  type IncidentSeverity, type IncidentStatus,
} from '@cleanblue/shared';
import { api } from '@/lib/api';
import { useApi, useApiMutation } from '@/lib/hooks';
import {
  Async, Badge, Button, Card, Cell, Empty, Field, Modal, Note, PageHead, StatusBadge, Table,
} from '@/components/ui';

interface Incident {
  id: string; unit_code: string; unit_name: string | null; category: string;
  title: string; description: string | null; severity: IncidentSeverity;
  status: IncidentStatus; resolution: string | null; created_at: string;
  submitted_by_name: string | null; assigned_name: string | null;
}

export function IncidentsPage() {
  const query = useApi<{ incidents: Incident[] }>('/admin/incidents');
  const [resolving, setResolving] = useState<Incident | null>(null);

  return (
    <>
      <PageHead
        title="Incidencias"
        subtitle="Daños, fallas de servicio y reclamos. El cliente ve el estado desde su portal."
      />

      <Async query={query} empty={(d) => d.incidents.length === 0 && (
        <Empty icon="confirmar" title="Ninguna incidencia registrada" hint="Buena señal." />
      )}>
        {(data) => {
          const open = data.incidents.filter((i) => i.status !== 'RESOLVED' && i.status !== 'CLOSED');
          return (
            <>
              {open.length > 0 && (
                <Note tone="warn">
                  <strong>{open.length}</strong> incidencia{open.length === 1 ? '' : 's'} sin resolver.
                </Note>
              )}

              <Card flush>
                <Table head={
                  <tr><th>Consultorio</th><th>Incidencia</th><th>Gravedad</th><th>Estado</th><th /></tr>
                }>
                  {data.incidents.map((i) => (
                    <tr key={i.id}>
                      <td className="strong"><Cell sub={i.created_at.slice(0, 10)}>{i.unit_code}</Cell></td>
                      <td>
                        <Cell sub={[i.category, i.submitted_by_name].filter(Boolean).join(' · ')}>
                          {i.title}
                        </Cell>
                      </td>
                      <td>
                        <Badge tone={i.severity === 'HIGH' ? 'danger' : i.severity === 'NORMAL' ? 'warn' : 'neutral'}>
                          {INCIDENT_SEVERITY_LABEL[i.severity]}
                        </Badge>
                      </td>
                      <td>
                        <StatusBadge value={i.status} labels={INCIDENT_STATUS_LABEL} tones={INCIDENT_STATUS_TONE} />
                      </td>
                      <td className="right">
                        {i.status !== 'CLOSED' && (
                          <Button onClick={() => setResolving(i)}>Actualizar</Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </Table>
              </Card>
            </>
          );
        }}
      </Async>

      {resolving && <UpdateModal incident={resolving} onClose={() => setResolving(null)} />}
    </>
  );
}

function UpdateModal({ incident, onClose }: { incident: Incident; onClose: () => void }) {
  const [status, setStatus] = useState<IncidentStatus>(incident.status);
  const [resolution, setResolution] = useState(incident.resolution ?? '');
  const needsResolution = status === 'RESOLVED' || status === 'CLOSED';

  const mutation = useApiMutation(
    () => api.post(`/admin/incidents/${incident.id}/update`, {
      status, resolution: resolution || undefined,
    }),
    ['/admin/incidents', '/admin/dashboard'],
    { onSuccess: onClose },
  );

  return (
    <Modal
      title={incident.title}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancelar</Button>
          <Button
            variant="primary"
            disabled={mutation.isPending || (needsResolution && resolution.length < 3)}
            onClick={() => mutation.mutate()}
          >
            Guardar
          </Button>
        </>
      }
    >
      {mutation.error && <Note tone="danger">{mutation.error.message}</Note>}

      {incident.description && <p className="small muted mb-2">{incident.description}</p>}

      <Field label="Estado" required>
        <select className="select" value={status} onChange={(e) => setStatus(e.target.value as IncidentStatus)}>
          {(Object.keys(INCIDENT_STATUS_LABEL) as IncidentStatus[]).map((s) => (
            <option key={s} value={s}>{INCIDENT_STATUS_LABEL[s]}</option>
          ))}
        </select>
      </Field>

      <Field
        label="Cómo se resolvió"
        required={needsResolution}
        hint="El cliente lo lee en su portal. Cerrar sin explicar no es cerrar."
      >
        <textarea className="input" rows={3} value={resolution} onChange={(e) => setResolution(e.target.value)} />
      </Field>
    </Modal>
  );
}
